<template>
  <div
    v-if="visibleBlocks.length"
    class="mb-25"
  >
    <div
      v-for="block in visibleBlocks"
      :key="block.id"
      :style="block.marginBottom ? { marginBottom: `${block.marginBottom}px` } : undefined"
    >
      <!-- Clickable Image -->
      <a
        v-if="block.type === 'image' && imageUrl(block)"
        :href="block.linkUrl || undefined"
        :target="block.linkUrl && block.openInNewTab ? '_blank' : '_self'"
        rel="noopener noreferrer"
        :style="{ display: 'block', lineHeight: '0', ...blockRadiusStyle(block.blockRadius), overflow: block.blockRadius ? 'hidden' : undefined }"
      >
        <img
          :src="imageUrl(block)"
          :alt="block.alt || ''"
          loading="lazy"
          style="width:100%; height:auto; display:block;"
        >
      </a>

      <!-- Text Card -->
      <div
        v-else-if="block.type === 'text'"
        :style="Object.assign({
          background: overlayBackground(block),
          color: '#ffffff',
          padding: block.contentWidth === 'full' ? '0' : '1.5rem',
          display: 'flex',
          boxSizing: 'border-box',
        }, contentPositionStyle(block), blockRadiusStyle(block.contentRadius))"
      >
        <div :style="contentWidthStyle(block)">
          <div
            v-if="block.body"
            class="banner-body"
            :style="{ fontSize: '1.1rem', marginTop: '0', lineHeight: '1.6' }"
            v-html="sanitize(block.body)"
          />
        </div>
      </div>

      <!-- Image with Text -->
      <div
        v-else-if="block.type === 'overlay'"
        :style="Object.assign({
          backgroundImage: imageUrl(block) ? `url(${imageUrl(block)})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundAttachment: 'scroll',
          backgroundColor: '#000',
          display: 'flex',
          padding: block.contentWidth === 'full' ? '0' : '1.5rem',
          boxSizing: 'border-box',
          overflow: block.blockRadius ? 'hidden' : undefined,
        }, overlayHeightStyle(block), contentPositionStyle(block), blockRadiusStyle(block.blockRadius))"
      >
        <div
          class="banner-overlay-content"
          :style="{
            background: overlayBackground(block),
            ...contentWidthStyle(block, true),
            ...blockRadiusStyle(block.contentRadius),
            color: '#ffffff',
            padding: '1.5rem',
            boxSizing: 'border-box',
          }"
        >
          <div
            v-if="block.body"
            class="banner-body"
            :style="{ fontSize: '1.1rem', marginTop: '0', lineHeight: '1.6' }"
            v-html="sanitize(block.body)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ProfileBanner',

  computed: {
    ...mapState(['artist']),

    visibleBlocks() {
      const blocks = this.artist?.profile?.configuration?.bannerBlocks
      if (!Array.isArray(blocks)) { return [] }
      return blocks.filter(b => b && b.enabled !== false)
    }
  },

  methods: {
    sanitize(html) {
      if (!html) return ''
      if (process.client) {
        // dompurify is browser-only; process.client=false on server → dead code eliminated
        // eslint-disable-next-line global-require
        const DOMPurify = require('dompurify')
        const clean = DOMPurify.sanitize(html)
        // Propagate inline span colors to <li> so ::marker inherits the right color
        const doc = new DOMParser().parseFromString(clean, 'text/html')
        doc.querySelectorAll('li').forEach((li) => {
          const span = li.querySelector('p > span[style*="color"]')
          if (span && span.style.color) { li.style.color = span.style.color }
        })
        return doc.body.innerHTML
      }
      return html
    },
    imageUrl(block) {
      if (!block.image) { return '' }
      if (block.image.name && this.artist?._id) {
        return `/services/media/image/${this.artist._id}/${block.image.name}/1200/0/image.webp`
      }
      return block.image.previewUrl || ''
    },
    overlayHeightStyle(block) {
      const h = block.blockHeight || '16/9'
      if (h === '100vh') return { minHeight: '100vh' }
      return { aspectRatio: h }
    },
    contentWidthStyle(block, isOverlay = false) {
      const w = block.contentWidth || 'standard'
      if (w === 'fit') { return { width: 'fit-content' } }
      const maxWidth = { narrow: '480px', standard: '720px', wide: '960px', full: '100%' }[w] || '720px'
      return isOverlay ? { maxWidth, width: '100%' } : { maxWidth }
    },
    contentPositionStyle(block) {
      const pos = block.contentPosition || 'center'
      const parts = pos === 'center' ? ['center', 'center'] : pos.split('-')
      const vMap = { top: 'flex-start', center: 'center', bottom: 'flex-end' }
      const hMap = { left: 'flex-start', center: 'center', right: 'flex-end' }
      return { alignItems: vMap[parts[0]], justifyContent: hMap[parts[1]] }
    },
    blockRadiusStyle(v) {
      const n = (!v || typeof v === 'number') ? v ?? 0 : null
      if (n !== null) return { borderRadius: `${n}px ${n}px ${n}px ${n}px` }
      return { borderRadius: `${v.tl ?? 0}px ${v.tr ?? 0}px ${v.br ?? 0}px ${v.bl ?? 0}px` }
    },
    overlayBackground(block) {
      const c = block.overlayColor || 'rgba(0,0,0,0.45)'
      const c2 = block.overlayColor2 || 'rgba(0,0,0,0)'
      if (block.overlayType === 'gradient-up')
        return `linear-gradient(to top, ${c} 0%, ${c2} 100%)`
      if (block.overlayType === 'gradient-down')
        return `linear-gradient(to bottom, ${c} 0%, ${c2} 100%)`
      if (block.overlayType === 'gradient-left')
        return `linear-gradient(to left, ${c} 0%, ${c2} 100%)`
      if (block.overlayType === 'gradient-right')
        return `linear-gradient(to right, ${c} 0%, ${c2} 100%)`
      return c
    }
  }
}
</script>

<style scoped>
.banner-body :deep(h1) { font-size: 2rem; font-weight: 700; line-height: 1.2; margin: 0 0 0.75rem; }
.banner-body :deep(h2) { font-size: 1.6rem; font-weight: 600; line-height: 1.2; margin: 0 0 0.6rem; }
.banner-body :deep(h3) { font-size: 1.3rem; font-weight: 600; line-height: 1.3; margin: 0 0 0.5rem; }
.banner-body :deep(p) { margin: 0 0 0.5rem; }
.banner-body :deep(ul) { list-style-type: disc; padding-left: 1.5em; margin: 0 0 0.5rem; }
.banner-body :deep(ol) { list-style-type: decimal; padding-left: 1.5em; margin: 0 0 0.5rem; }
.banner-body :deep(ul:has(> li > p[style*="text-align: center"])),
.banner-body :deep(ol:has(> li > p[style*="text-align: center"])) { width: fit-content; margin-left: auto; margin-right: auto; }
.banner-body :deep(ul:has(> li > p[style*="text-align: right"])),
.banner-body :deep(ol:has(> li > p[style*="text-align: right"])) { width: fit-content; margin-left: auto; margin-right: 0; }
.banner-body :deep(li) { line-height: 1.6; }
.banner-body :deep(*:last-child) { margin-bottom: 0; }
.banner-body :deep(strong), .banner-body :deep(b) { color: inherit; }

/* Overlay content scales fonts/padding relative to its own width via container queries.
   cqi = 1% of the container's inline (width) size — correct in editor preview and all viewports. */
.banner-overlay-content {
  container-type: inline-size;
  padding: clamp(0.75rem, 2.5cqi, 1.5rem) !important;
}

@media (max-width: 768px) {
  .banner-overlay-content {
    max-width: 100% !important;
    width: 100% !important;
  }
}

.banner-overlay-content .banner-body { font-size: clamp(0.75rem, 2.5cqi, 1.1rem) !important; line-height: 1.6 !important; }
.banner-overlay-content .banner-body :deep(h1) { font-size: clamp(1rem, 4.5cqi, 2rem); margin-bottom: clamp(0.25rem, 1cqi, 0.75rem); }
.banner-overlay-content .banner-body :deep(h2) { font-size: clamp(0.9rem, 3.5cqi, 1.6rem); margin-bottom: clamp(0.2rem, 0.8cqi, 0.6rem); }
.banner-overlay-content .banner-body :deep(h3) { font-size: clamp(0.85rem, 3cqi, 1.3rem); margin-bottom: clamp(0.2rem, 0.7cqi, 0.5rem); }
.banner-overlay-content .banner-body :deep(p) { margin-bottom: clamp(0.15rem, 0.6cqi, 0.5rem); }
.banner-overlay-content .banner-body :deep(p[data-type="block-button"] a) {
  padding: clamp(0.4rem, 1.2cqi, 0.75rem) clamp(0.75rem, 2.5cqi, 1.5rem) !important;
  font-size: clamp(0.75rem, 2cqi, 1rem) !important;
}
</style>
